// Typed fetch client for the Bakeoff REST API. All calls send the session
// cookie (same-origin) and throw ApiError with a friendly message on failure.
import type {
  BankQuestionDetail,
  GameMode,
  QuestionInput,
  QuestionType,
  QuizDetail,
  QuizListItem,
  RecycleBinItem,
  ResultDetail,
  ResultSummary,
  ScoringMode,
  SessionSettings,
  SessionUser,
  SharedQuizListItem,
  TeamMember,
  UserSuggestion,
  ActivityLogItem,
  AdminQuizItem,
  LiveSessionItem,
} from "../shared/types";

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function req<T>(path: string, opts: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    credentials: "same-origin",
    headers: opts.body ? { "Content-Type": "application/json" } : undefined,
    ...opts,
  });
  let data: any = null;
  try {
    data = await res.json();
  } catch {
    /* no body */
  }
  if (!res.ok) throw new ApiError(data?.error || res.statusText || "Request failed", res.status);
  return data as T;
}

const body = (v: unknown) => JSON.stringify(v);

/** The save payload for a quiz (server regenerates ids/positions/keys). */
export interface QuizSaveBody {
  title: string;
  description?: string;
  scoring: ScoringMode;
  status?: "draft" | "published";
  coverEmoji?: string;
  questions: {
    type: QuestionType;
    prompt: string;
    category?: string;
    chefNote?: string;
    timeLimit: number;
    points: number;
    imageKey?: string;
    options: { text: string; correct: boolean; imageKey?: string }[];
  }[];
}

export interface UploadResult {
  key: string;
  url: string;
  contentType: string;
}

export const api = {
  // ── auth ──
  me: () => req<{ user: SessionUser; impersonator?: SessionUser }>("/api/auth/me"),
  bootstrapStatus: () => req<{ needsBootstrap: boolean }>("/api/auth/bootstrap"),
  login: (username: string, password: string) =>
    req<{ user: SessionUser }>("/api/auth/login", { method: "POST", body: body({ username, password }) }),
  signup: (username: string, password: string, displayName: string) =>
    req<{ user: SessionUser }>("/api/auth/signup", {
      method: "POST",
      body: body({ username, password, displayName }),
    }),
  logout: () => req<{ ok: boolean }>("/api/auth/logout", { method: "POST" }),
  impersonate: (id: string) =>
    req<{ user: SessionUser }>(`/api/auth/users/${id}/impersonate`, { method: "POST" }),
  stopImpersonating: () =>
    req<{ user: SessionUser }>("/api/auth/stop-impersonating", { method: "POST" }),
  changePassword: (currentPassword: string, newPassword: string) =>
    req<{ ok: boolean }>("/api/auth/password", {
      method: "POST",
      body: body({ currentPassword, newPassword }),
    }),
  listUsers: () => req<{ users: TeamMember[] }>("/api/auth/users"),
  createUser: (username: string, password: string, displayName: string, role: "admin" | "host") =>
    req<{ user: SessionUser }>("/api/auth/users", {
      method: "POST",
      body: body({ username, password, displayName, role }),
    }),
  updateUser: (
    id: string,
    patch: { displayName?: string; username?: string; role?: "admin" | "host"; password?: string },
  ) =>
    req<{ user: SessionUser }>(`/api/auth/users/${id}`, { method: "PATCH", body: body(patch) }),
  deleteUser: (id: string) => req<{ ok: boolean }>(`/api/auth/users/${id}`, { method: "DELETE" }),
  listRecycleBin: () => req<{ quizzes: RecycleBinItem[] }>("/api/recycle-bin"),
  restoreFromBin: (id: string, ownerId: string) =>
    req<{ ok: boolean }>(`/api/recycle-bin/${id}/restore`, { method: "POST", body: body({ ownerId }) }),
  purgeFromBin: (id: string) => req<{ ok: boolean }>(`/api/recycle-bin/${id}`, { method: "DELETE" }),
  emptyBin: () => req<{ removed: number }>("/api/recycle-bin/empty", { method: "POST" }),

  // ── super-admin areas (Logs / All Quizzes / Live) ──
  listActivity: (opts: { action?: string; before?: number; limit?: number } = {}) => {
    const p = new URLSearchParams();
    if (opts.action) p.set("action", opts.action);
    if (opts.before) p.set("before", String(opts.before));
    if (opts.limit) p.set("limit", String(opts.limit));
    const qs = p.toString();
    return req<{ items: ActivityLogItem[] }>(`/api/admin/activity${qs ? `?${qs}` : ""}`);
  },
  clearActivity: () => req<{ removed: number }>("/api/admin/activity", { method: "DELETE" }),
  listAllQuizzes: () => req<{ quizzes: AdminQuizItem[] }>("/api/admin/quizzes"),
  listLiveSessions: () => req<{ sessions: LiveSessionItem[] }>("/api/admin/live"),
  endLiveSession: (hostId: string) =>
    req<{ ok: boolean }>(`/api/admin/live/${hostId}/end`, { method: "POST" }),

  // ── quizzes ──
  listQuizzes: () => req<{ quizzes: QuizListItem[] }>("/api/quizzes"),
  getQuiz: (id: string) => req<{ quiz: QuizDetail }>(`/api/quizzes/${id}`),
  createQuiz: (title: string, scoring: ScoringMode, source?: "ai") =>
    req<{ quiz: QuizDetail }>("/api/quizzes", { method: "POST", body: body({ title, scoring, source }) }),
  saveQuiz: (id: string, payload: QuizSaveBody) =>
    req<{ quiz: QuizDetail }>(`/api/quizzes/${id}`, { method: "PUT", body: body(payload) }),
  deleteQuiz: (id: string) => req<{ ok: boolean }>(`/api/quizzes/${id}`, { method: "DELETE" }),
  duplicateQuiz: (id: string) =>
    req<{ quiz: QuizDetail }>(`/api/quizzes/${id}/duplicate`, { method: "POST" }),
  cleanupEmptyDrafts: () =>
    req<{ removed: number }>("/api/quizzes/cleanup-empty", { method: "POST" }),
  listSharedQuizzes: () => req<{ quizzes: SharedQuizListItem[] }>("/api/quizzes/shared"),
  shareQuiz: (id: string, userId: string) =>
    req<{ ok: boolean }>(`/api/quizzes/${id}/share`, { method: "POST", body: body({ userId }) }),
  unshareQuiz: (id: string, userId: string) =>
    req<{ ok: boolean }>(`/api/quizzes/${id}/share/${userId}`, { method: "DELETE" }),
  leaveSharedQuiz: (id: string) =>
    req<{ ok: boolean }>(`/api/quizzes/${id}/share`, { method: "DELETE" }),
  listQuizShares: (id: string) =>
    req<{ users: UserSuggestion[] }>(`/api/quizzes/${id}/shares`),
  searchUsers: (q: string) =>
    req<{ users: UserSuggestion[] }>(`/api/users/search?q=${encodeURIComponent(q)}`),

  // ── question bank ──
  listBank: () => req<{ questions: BankQuestionDetail[] }>("/api/bank"),
  createBankQuestion: (q: QuestionInput) =>
    req<{ question: BankQuestionDetail }>("/api/bank", { method: "POST", body: body(q) }),
  updateBankQuestion: (id: string, q: QuestionInput) =>
    req<{ question: BankQuestionDetail }>(`/api/bank/${id}`, { method: "PUT", body: body(q) }),
  deleteBankQuestion: (id: string) =>
    req<{ ok: boolean }>(`/api/bank/${id}`, { method: "DELETE" }),

  // ── live sessions ──
  createSession: (quizId: string, settings?: SessionSettings) =>
    req<{ pin: string; hostToken: string }>("/api/sessions", {
      method: "POST",
      body: body({ quizId, settings }),
    }),
  peekSession: (pin: string) =>
    req<{ found: boolean; joinable: boolean; quizTitle: string; hostName?: string; phase: string; mode?: GameMode; playerCount: number }>(
      `/api/sessions/${pin}`,
    ),
  activeSession: () =>
    req<{ active: null | { pin: string; quizTitle: string; hostToken: string } }>("/api/sessions/mine"),

  // ── results ──
  listResults: () => req<{ results: ResultSummary[] }>("/api/results"),
  getResult: (id: string) => req<{ result: ResultDetail }>(`/api/results/${id}`),
  deleteResult: (id: string) => req<{ ok: boolean }>(`/api/results/${id}`, { method: "DELETE" }),

  // ── media uploads ──
  async upload(file: File | Blob, contentType?: string, signal?: AbortSignal): Promise<UploadResult> {
    const res = await fetch("/api/uploads", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": contentType || (file as File).type || "application/octet-stream" },
      body: file,
      signal,
    });
    let data: any = null;
    try {
      data = await res.json();
    } catch {
      /* none */
    }
    if (!res.ok) throw new ApiError(data?.error || "Upload failed", res.status);
    return data as UploadResult;
  },

  // ── AI quiz generation ──
};

/** Generate a quiz, streaming progress. Calls `onProgress(done, total)` as
    questions are drafted; resolves with the (un-persisted) draft to review. */
export async function generateQuizStream(
  input: { topic?: string; count: number; scoring: ScoringMode; sourceKey?: string },
  onProgress: (done: number, total: number) => void,
  signal?: AbortSignal,
): Promise<QuizSaveBody> {
  const res = await fetch("/api/ai/generate", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
    signal,
  });
  if (!res.ok || !res.body) {
    let msg = "Generation failed — give it another go.";
    try {
      const j = (await res.json()) as { error?: string };
      if (j?.error) msg = j.error;
    } catch {
      /* no JSON body */
    }
    throw new ApiError(msg, res.status);
  }

  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  let draft: QuizSaveBody | null = null;
  let errMsg: string | null = null;

  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    let nl: number;
    while ((nl = buf.indexOf("\n")) >= 0) {
      const line = buf.slice(0, nl).trim();
      buf = buf.slice(nl + 1);
      if (!line) continue;
      let ev: { t: string; done?: number; total?: number; draft?: QuizSaveBody; message?: string };
      try {
        ev = JSON.parse(line);
      } catch {
        continue;
      }
      if (ev.t === "progress") onProgress(ev.done ?? 0, ev.total ?? 0);
      else if (ev.t === "done") draft = ev.draft ?? null;
      else if (ev.t === "error") errMsg = ev.message ?? "Generation failed.";
    }
  }

  if (errMsg) throw new ApiError(errMsg, 502);
  if (!draft) throw new ApiError("Generation produced no questions.", 502);
  return draft;
}

/** Start hosting a quiz: create the session, stash the host token, return the PIN. */
export async function hostQuiz(quizId: string, settings?: SessionSettings): Promise<string> {
  const { pin, hostToken } = await api.createSession(quizId, settings);
  try {
    localStorage.setItem(`bk_host_${pin}`, hostToken);
  } catch {
    /* private mode — host can still drive this tab via in-memory token */
  }
  return pin;
}

export function hostTokenFor(pin: string): string | null {
  try {
    return localStorage.getItem(`bk_host_${pin}`);
  } catch {
    return null;
  }
}

/** Stash a host token (e.g. when resuming a session from another device). */
export function rememberHostToken(pin: string, token: string): void {
  try {
    localStorage.setItem(`bk_host_${pin}`, token);
  } catch {
    /* private mode — token lives only in this tab's memory */
  }
}

// ── Player session persistence (survive a refresh → auto-rejoin same game) ──
const PLAYER_KEY = "bk_player";

export function rememberPlayer(pin: string, name: string, emoji?: string, token?: string): void {
  try {
    localStorage.setItem(PLAYER_KEY, JSON.stringify({ pin, name, emoji, token }));
  } catch {
    /* private mode — they'll just re-enter the PIN on refresh */
  }
}

export function storedPlayer(): { pin: string; name: string; emoji?: string; token?: string } | null {
  try {
    const r = JSON.parse(localStorage.getItem(PLAYER_KEY) || "null");
    return r && typeof r.pin === "string" && typeof r.name === "string" && r.pin && r.name ? r : null;
  } catch {
    return null;
  }
}

export function forgetPlayer(): void {
  try {
    localStorage.removeItem(PLAYER_KEY);
  } catch {
    /* ignore */
  }
}
